import React from "react";

/**
 * Safe Lazy Import Wrapper with Download Logs
 * 
 * Wraps React.lazy() to prevent app crashes when widget files fail to load.
 * Instead of throwing errors, it gracefully falls back to a fallback component.
 * Includes comprehensive logging for download start, success, and failure.
 * 
 * @param importer - The dynamic import function (e.g., () => import('./Component'))
 * @param fallbackComponent - Component to render if import fails
 * @returns A React lazy component that never throws on load failure
 * 
 * @example
 * const SafeWidget = safeLazy(
 *   () => import('./MyWidget'),
 *   FallbackWidget
 * );
 */
export function safeLazy<T extends React.ComponentType<any>>(
  importer: () => Promise<{ default: T }>,
  fallbackComponent: React.ComponentType<any>
) {
  return React.lazy(() => {
    return importer()
      .then(module => {
        // Successfully loaded the module
        return { default: module.default };
      })
      .catch(err => {
        // Handle any load errors (404, network, chunk load, etc.)
        console.error("❌ Widget download failed → Using fallback", {
          error: err.message || err,
          stack: err.stack,
        });
        
        // Return the fallback component instead of crashing
        return { default: fallbackComponent };
      });
  });
}
